export interface TimezoneOption {
  value: string;
  label: string;
}

const US_STATE_TIMEZONE: Record<string, string> = {
  AL: 'America/Chicago',
  AK: 'America/Anchorage',
  AZ: 'America/Phoenix',
  AR: 'America/Chicago',
  CA: 'America/Los_Angeles',
  CO: 'America/Denver',
  CT: 'America/New_York',
  DE: 'America/New_York',
  DC: 'America/New_York',
  FL: 'America/New_York',
  GA: 'America/New_York',
  HI: 'Pacific/Honolulu',
  ID: 'America/Denver',
  IL: 'America/Chicago',
  IN: 'America/Indiana/Indianapolis',
  IA: 'America/Chicago',
  KS: 'America/Chicago',
  KY: 'America/New_York',
  LA: 'America/Chicago',
  ME: 'America/New_York',
  MD: 'America/New_York',
  MA: 'America/New_York',
  MI: 'America/Detroit',
  MN: 'America/Chicago',
  MS: 'America/Chicago',
  MO: 'America/Chicago',
  MT: 'America/Denver',
  NE: 'America/Chicago',
  NV: 'America/Los_Angeles',
  NH: 'America/New_York',
  NJ: 'America/New_York',
  NM: 'America/Denver',
  NY: 'America/New_York',
  NC: 'America/New_York',
  ND: 'America/Chicago',
  OH: 'America/New_York',
  OK: 'America/Chicago',
  OR: 'America/Los_Angeles',
  PA: 'America/New_York',
  RI: 'America/New_York',
  SC: 'America/New_York',
  SD: 'America/Chicago',
  TN: 'America/Chicago',
  TX: 'America/Chicago',
  UT: 'America/Denver',
  VT: 'America/New_York',
  VA: 'America/New_York',
  WA: 'America/Los_Angeles',
  WV: 'America/New_York',
  WI: 'America/Chicago',
  WY: 'America/Denver',
};

const AU_STATE_TIMEZONE: Record<string, string> = {
  ACT: 'Australia/Sydney',
  NSW: 'Australia/Sydney',
  NT: 'Australia/Darwin',
  QLD: 'Australia/Brisbane',
  SA: 'Australia/Adelaide',
  TAS: 'Australia/Hobart',
  VIC: 'Australia/Melbourne',
  WA: 'Australia/Perth',
};

const COUNTRY_DEFAULT_TIMEZONE: Record<string, string> = {
  US: 'America/New_York',
  AU: 'Australia/Sydney',
  GB: 'Europe/London',
  CA: 'America/Toronto',
  NZ: 'Pacific/Auckland',
  IE: 'Europe/Dublin',
  DE: 'Europe/Berlin',
  FR: 'Europe/Paris',
  ES: 'Europe/Madrid',
  IT: 'Europe/Rome',
  NL: 'Europe/Amsterdam',
  BE: 'Europe/Brussels',
  AT: 'Europe/Vienna',
  CH: 'Europe/Zurich',
  SE: 'Europe/Stockholm',
  NO: 'Europe/Oslo',
  DK: 'Europe/Copenhagen',
  FI: 'Europe/Helsinki',
  PT: 'Europe/Lisbon',
  PL: 'Europe/Warsaw',
  JP: 'Asia/Tokyo',
  SG: 'Asia/Singapore',
  HK: 'Asia/Hong_Kong',
  IN: 'Asia/Kolkata',
  AE: 'Asia/Dubai',
  ZA: 'Africa/Johannesburg',
  MX: 'America/Mexico_City',
  BR: 'America/Sao_Paulo',
};

const COUNTRY_TIMEZONE_OPTIONS: Record<string, readonly string[]> = {
  US: [
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'America/Phoenix',
    'America/Anchorage',
    'Pacific/Honolulu',
    'America/Detroit',
    'America/Indiana/Indianapolis',
  ],
  AU: [
    'Australia/Sydney',
    'Australia/Melbourne',
    'Australia/Brisbane',
    'Australia/Adelaide',
    'Australia/Perth',
    'Australia/Hobart',
    'Australia/Darwin',
  ],
  CA: [
    'America/Toronto',
    'America/Vancouver',
    'America/Edmonton',
    'America/Winnipeg',
    'America/Halifax',
    'America/St_Johns',
  ],
  GB: ['Europe/London'],
  NZ: ['Pacific/Auckland', 'Pacific/Chatham'],
};

const COMMON_TIMEZONE_OPTIONS: readonly string[] = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Toronto',
  'America/Vancouver',
  'America/Mexico_City',
  'America/Sao_Paulo',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Madrid',
  'Europe/Rome',
  'Europe/Amsterdam',
  'Europe/Stockholm',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Asia/Hong_Kong',
  'Australia/Perth',
  'Australia/Adelaide',
  'Australia/Brisbane',
  'Australia/Sydney',
  'Pacific/Auckland',
];

function normalizeCountryCode(countryCode?: string | null): string {
  return countryCode?.trim().toUpperCase() ?? '';
}

function normalizeStateCode(stateCode?: string | null): string {
  return stateCode?.trim().toUpperCase() ?? '';
}

export function formatTimezoneLabel(timezone: string): string {
  const trimmed = timezone.trim();
  if (!trimmed) return '';

  const city = trimmed.split('/').pop()?.replace(/_/g, ' ') ?? trimmed;

  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: trimmed,
      timeZoneName: 'short',
    });
    const abbr = formatter.formatToParts(new Date()).find((part) => part.type === 'timeZoneName')?.value;
    return abbr ? `${city} (${abbr})` : city;
  } catch {
    return city;
  }
}

export function inferTimezoneFromLocation(
  countryCode?: string | null,
  stateOrProvince?: string | null
): string | undefined {
  const country = normalizeCountryCode(countryCode);
  const state = normalizeStateCode(stateOrProvince);

  if (!country) return undefined;

  if (country === 'US' && state) {
    return US_STATE_TIMEZONE[state];
  }

  if (country === 'AU' && state) {
    return AU_STATE_TIMEZONE[state];
  }

  return COUNTRY_DEFAULT_TIMEZONE[country];
}

export function getTimezoneOptionsForLocation(
  countryCode?: string | null,
  stateOrProvince?: string | null
): TimezoneOption[] {
  const country = normalizeCountryCode(countryCode);
  const state = normalizeStateCode(stateOrProvince);
  const inferred = inferTimezoneFromLocation(country, state);

  const values = new Set<string>();
  if (inferred) values.add(inferred);

  const countryOptions = country ? COUNTRY_TIMEZONE_OPTIONS[country] : undefined;
  if (countryOptions) {
    for (const tz of countryOptions) values.add(tz);
  } else if (country && COUNTRY_DEFAULT_TIMEZONE[country]) {
    values.add(COUNTRY_DEFAULT_TIMEZONE[country]);
  } else {
    for (const tz of COMMON_TIMEZONE_OPTIONS) values.add(tz);
  }

  return [...values]
    .sort((a, b) => formatTimezoneLabel(a).localeCompare(formatTimezoneLabel(b)))
    .map((value) => ({
      value,
      label: formatTimezoneLabel(value),
    }));
}
